import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Chip, Paper, Stack, Typography, FormLabel } from '@mui/material'
import { CommonButton } from '@/components/common'
import { OperationStatusChip } from '@/components/common/OperationStatusChip'
import { DescriptionDialog } from '@/components/donation/description-dialog'
import { buildDescriptionItem } from '@/utils'
import { ResultCardSkeleton } from '@/pages/donate-near-you/results/Skeletons'

export const ResultCard = ({
  item = {},
  selected: selectedProp,
  isSelectDisabled,
  onSelect,
  showDescription = false,
  isClickable = true,
  loading = false,
  date,
  operationDate,
  onViewDonorSchedule,
  onViewDonors,
  onViewResults,
  compactActions = false,
  isShowingDonorSchedule = false,
  /** 'fullCard' dims card and uses whole card as tap target; 'buttonOnly' keeps card bright and schedules only via Select. */
  selectionSurface = 'fullCard',
  /** Logged-in eligibility chip: { label, status: 'eligible' | 'not_eligible' } */
  eligibilityChip = null,
}) => {
  const [selectedInternal, setSelectedInternal] = useState(!!selectedProp)
  const [openDesc, setOpenDesc] = useState(false)

  const selected = selectedProp ?? selectedInternal

  const handleSelect = e => {
    e?.stopPropagation?.()
    if (isSelectDisabled) return
    if (selectedProp === undefined) setSelectedInternal(!selected)
    if (onSelect) onSelect(item)
  }

  const handleOpenDesc = e => {
    e?.stopPropagation?.()
    setOpenDesc(true)
  }

  const handleCloseDesc = () => setOpenDesc(false)

  const {
    name,
    sponsoredBy,
    distance,
    secondaryName,
    hours,
    address,
    spotsAvailable,
    operation_status_name: operationStatusName,
    operation_status_chip_color: operationStatusChipColor,
  } = item

  const descriptionItem = useMemo(
    () => buildDescriptionItem(item),
    [openDesc, item]
  )

  if (loading) {
    return <ResultCardSkeleton />
  }

  const showButtons = isClickable && !date && !onViewDonorSchedule

  const scheduleBlocked = !!isSelectDisabled
  const useFullCardSelect =
    selectionSurface === 'fullCard' &&
    isClickable &&
    !scheduleBlocked &&
    showButtons
  const paperScheduleClick = useFullCardSelect ? handleSelect : undefined

  const cardOpacity =
    selectionSurface === 'buttonOnly' ? 1 : scheduleBlocked ? 0.72 : 1
  const cardCursor =
    selectionSurface === 'buttonOnly'
      ? 'default'
      : !isClickable || scheduleBlocked
        ? 'default'
        : 'pointer'

  const actionButtonSize = 'medium'
  const actionButtonWidth = compactActions ? '140px' : '120px'
  const actionStackMinWidth = compactActions ? 140 : 140
  const actionStackSpacing = 1
  const useDriveCardLayout =
    compactActions && showButtons && (onViewDonors || onViewResults)

  const metadataRows = (
    <>
      {hours && (
        <Typography variant='body1'>
          <FormLabel
            component='span'
            sx={{ display: { xs: 'none', md: 'inline' } }}
          >
            Hours:
          </FormLabel>{' '}
          {hours}
        </Typography>
      )}
      {address && (
        <Typography variant='body1'>
          <FormLabel
            component='span'
            sx={{ display: { xs: 'none', md: 'inline' } }}
          >
            Address:
          </FormLabel>{' '}
          {address}
        </Typography>
      )}
      {spotsAvailable != null && spotsAvailable !== '' && (
        <Typography variant='body1'>
          <FormLabel component='span'>Appointments:</FormLabel> {spotsAvailable}
        </Typography>
      )}
    </>
  )

  return (
    <Paper
      elevation={0}
      onClick={paperScheduleClick}
      sx={{
        display: useDriveCardLayout ? 'grid' : 'flex',
        gridTemplateColumns: useDriveCardLayout
          ? { xs: '1fr', md: 'minmax(0, 1fr) auto' }
          : undefined,
        alignItems: 'stretch',
        gap: useDriveCardLayout ? { xs: 2, md: 3 } : 2,
        p: isClickable ? (useDriveCardLayout ? 2.5 : 2) : 0,
        width: '100%',
        cursor: cardCursor,
        opacity: cardOpacity,
        borderRadius: '6px',
        borderColor: selected ? 'primary.main' : 'transparent',
        backgroundColor: isClickable ? 'secondary.main' : 'transparent',
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          flex: useDriveCardLayout ? undefined : 1,
          minWidth: useDriveCardLayout ? { md: 0 } : 0,
          justifyContent: 'space-evenly',
          pr: useDriveCardLayout ? { md: 1 } : 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {name && (
                <Typography
                  variant='h6'
                  fontWeight={600}
                  sx={{
                    color: isClickable ? 'text.primary' : 'primary.main',
                    typography: { xs: 'body1', md: isClickable ? 'h6' : 'h5' },
                  }}
                >
                  {name}
                </Typography>
              )}
              {operationStatusName ? (
                <OperationStatusChip
                  label={operationStatusName}
                  chipColor={operationStatusChipColor}
                />
              ) : null}
            </Box>

            {onViewDonorSchedule && (
              <Stack spacing={1} alignItems='flex-end' justifyContent='center'>
                <CommonButton
                  variant='text'
                  size='small'
                  onClick={e => {
                    e?.stopPropagation?.()
                    onViewDonorSchedule()
                  }}
                >
                  {isShowingDonorSchedule
                    ? 'Schedule Appointment'
                    : 'View Donor Schedule'}
                </CommonButton>
                {onViewDonors ? (
                  <CommonButton
                    variant='text'
                    size='small'
                    onClick={e => {
                      e?.stopPropagation?.()
                      onViewDonors(item)
                    }}
                  >
                    Donors
                  </CommonButton>
                ) : null}
                {onViewResults ? (
                  <CommonButton
                    variant='text'
                    size='small'
                    onClick={e => {
                      e?.stopPropagation?.()
                      onViewResults(item)
                    }}
                  >
                    View Results
                  </CommonButton>
                ) : null}
              </Stack>
            )}
          </Box>

          {sponsoredBy && (
            <Typography variant='body2'>
              • <FormLabel component='span'>Sponsored By:</FormLabel>{' '}
              {sponsoredBy}
            </Typography>
          )}
        </Box>
        {onViewDonorSchedule && date && (
          <Typography variant='body1' fontWeight={600}>
            {date}
          </Typography>
        )}

        {operationDate && (
          <Typography variant='subtitle1'>
            <FormLabel
              component='span'
              sx={{ display: { xs: 'none', md: 'inline' } }}
            >
              Date:
            </FormLabel>{' '}
            {operationDate}
          </Typography>
        )}
        {distance != null && (
          <Typography variant='subtitle1'>
            <FormLabel
              component='span'
              sx={{ display: { xs: 'none', md: 'inline' } }}
            >
              Distance:
            </FormLabel>{' '}
            {distance} mi
          </Typography>
        )}
        {secondaryName && (
          <Typography variant='body2'>{secondaryName}</Typography>
        )}

        {useDriveCardLayout ? (
          <Stack spacing={0.75}>{metadataRows}</Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justfyContent: 'flex-end',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            {hours && (
              <Typography variant='body1'>
                <FormLabel
                  component='span'
                  sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                  Hours:
                </FormLabel>{' '}
                {hours}
              </Typography>
            )}
            {hours && (address || spotsAvailable) && (
              <Typography
                variant='body1'
                sx={{ display: { xs: 'none', md: 'inline' } }}
              >
                •
              </Typography>
            )}
            {address && (
              <Typography variant='body1'>
                <FormLabel
                  component='span'
                  sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                  Address:
                </FormLabel>{' '}
                {address}
              </Typography>
            )}
            {address && spotsAvailable && (
              <Typography
                variant='body1'
                sx={{ display: { xs: 'none', md: 'inline' } }}
              >
                •
              </Typography>
            )}
            {spotsAvailable && (
              <Typography variant='body1'>
                <FormLabel component='span'>Appointments:</FormLabel>{' '}
                {spotsAvailable}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      {showButtons && (
        <Stack
          spacing={actionStackSpacing}
          alignItems='flex-end'
          justifyContent='center'
          sx={{
            minWidth: actionStackMinWidth,
            flexShrink: 0,
            alignSelf: useDriveCardLayout ? 'center' : undefined,
          }}
        >
          {eligibilityChip ? (
            <Chip
              size='small'
              label={eligibilityChip.label}
              color={
                eligibilityChip.status === 'eligible' ? 'success' : 'default'
              }
              variant={
                eligibilityChip.status === 'eligible' ? 'filled' : 'outlined'
              }
              sx={{
                fontWeight: 700,
                fontSize: '0.65rem',
                maxWidth: '100%',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ) : null}
          <CommonButton
            width={actionButtonWidth}
            size={actionButtonSize}
            onClick={handleSelect}
            disabled={isSelectDisabled}
            variant={selected ? 'primary' : 'secondary'}
          >
            {selected ? 'Selected' : 'Select'}
          </CommonButton>
          {onViewDonors ? (
            <CommonButton
              width={actionButtonWidth}
              size={actionButtonSize}
              variant='secondary'
              onClick={e => {
                e?.stopPropagation?.()
                onViewDonors(item)
              }}
            >
              Donors
            </CommonButton>
          ) : null}
          {onViewResults ? (
            <CommonButton
              width={actionButtonWidth}
              size={actionButtonSize}
              variant='secondary'
              onClick={e => {
                e?.stopPropagation?.()
                onViewResults(item)
              }}
            >
              View Results
            </CommonButton>
          ) : null}
          {showDescription && (
            <CommonButton
              variant='secondary'
              size={actionButtonSize}
              width={actionButtonWidth}
              onClick={handleOpenDesc}
            >
              Description
            </CommonButton>
          )}
        </Stack>
      )}
      {date && !onViewDonorSchedule && (
        <Stack spacing={1} alignItems='flex-end' sx={{ minWidth: 140 }}>
          <Typography
            fontWeight={500}
            sx={{ typography: { xs: 'body2', md: 'h5' } }}
          >
            {date}
          </Typography>
        </Stack>
      )}

      <DescriptionDialog
        open={openDesc}
        onClose={handleCloseDesc}
        item={descriptionItem}
      />
    </Paper>
  )
}

ResultCard.propTypes = {
  item: PropTypes.object,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  isSelectDisabled: PropTypes.bool,
  isClickable: PropTypes.bool,
  loading: PropTypes.bool,
  date: PropTypes.string,
  operationDate: PropTypes.string,
  onViewDonorSchedule: PropTypes.func,
  onViewDonors: PropTypes.func,
  onViewResults: PropTypes.func,
  compactActions: PropTypes.bool,
  isShowingDonorSchedule: PropTypes.bool,
  selectionSurface: PropTypes.oneOf(['fullCard', 'buttonOnly']),
  eligibilityChip: PropTypes.shape({
    label: PropTypes.string,
    status: PropTypes.oneOf(['eligible', 'not_eligible']),
  }),
}
